import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { apiService } from "../../api/api";
import {
  CreateOacPlaylistResponse,
  OacPlaylistPreviewResponse,
  PlaylistPrivacyStatus,
  oacPlaylistApi,
} from "../../api/oacPlaylistApi";
import { ChannelSettingsWithId } from "../../utils/@types";
import {
  getOacActivityStatus,
  oacActivityTagSeverity,
  OAC_ACTIVITY_STATUS_LABEL,
  OAC_ACTIVITY_STATUS_ORDER,
  OacActivityStatus,
  OacChannelLastUpdateRow,
} from "./oacActivityStatus";

type ChannelOption = {
  label: string;
  value: string;
  brand?: string;
  isOacAudit: boolean;
  activityStatus?: OacActivityStatus;
  activityLabel?: string;
};

const PRIVACY_OPTIONS: { label: string; value: PlaylistPrivacyStatus }[] = [
  { label: "Private", value: "private" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Public", value: "public" },
];

function brandLabel(ch: ChannelSettingsWithId): string {
  const brand = ch.Brand;
  if (Array.isArray(brand)) {
    const first = brand.map((b) => String(b).trim()).find(Boolean);
    if (first) return first;
  } else if (typeof brand === "string" && brand.trim()) {
    return brand.trim();
  }
  return (ch.OwnerShortName || ch.ChannelTitle || "").trim();
}

function formatDate(iso?: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  } catch {
    return iso;
  }
}

function ActivityTag({ status }: { status: OacActivityStatus }) {
  return (
    <Tag
      severity={oacActivityTagSeverity(status)}
      value={OAC_ACTIVITY_STATUS_LABEL[status]}
    />
  );
}

const PlaylistCreationTab = () => {
  const toast = useRef<Toast>(null);
  const [searchParams] = useSearchParams();
  const preselectedChannelId = searchParams.get("channelId")?.trim() || null;

  const [channelsLoading, setChannelsLoading] = useState(true);
  const [channelOptions, setChannelOptions] = useState<ChannelOption[]>([]);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<OacPlaylistPreviewResponse | null>(
    null
  );
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [privacyStatus, setPrivacyStatus] =
    useState<PlaylistPrivacyStatus>("private");
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<CreateOacPlaylistResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setChannelsLoading(true);
      try {
        const [settings, auditIds, lastUpdates] = await Promise.all([
          apiService.channelSettings.getAllChannelSettings(),
          apiService.oacAuditChannelIds.getChannelIds(),
          apiService.oacAuditChannelIds.getChannelLastUpdates().catch(() => ({
            data: [] as OacChannelLastUpdateRow[],
            updatedAt: null as string | null,
          })),
        ]);
        if (cancelled) return;

        const auditIdSet = new Set(
          (auditIds || []).map((id) => String(id).trim()).filter(Boolean)
        );
        const activityById = new Map<string, OacActivityStatus>();
        for (const row of lastUpdates.data || []) {
          activityById.set(row.channelId, getOacActivityStatus(row));
        }

        const list = (Array.isArray(settings) ? settings : [])
          .filter((ch) => (ch.YoutubeContentOwnerId || "").trim())
          .map((ch) => {
            const id = ch.ChannelId || ch.id;
            const brand = brandLabel(ch);
            const name = (ch.ChannelTitle || id).trim();
            const isOacAudit = auditIdSet.has(id);
            const activityStatus = isOacAudit
              ? activityById.get(id) ?? "noActivity"
              : undefined;
            const baseLabel = brand
              ? `${name} (${brand}) — ${id}`
              : `${name} — ${id}`;
            const activityLabel = activityStatus
              ? OAC_ACTIVITY_STATUS_LABEL[activityStatus]
              : undefined;
            return {
              label: activityLabel
                ? `[OAC · ${activityLabel}] ${baseLabel}`
                : baseLabel,
              value: id,
              brand,
              isOacAudit,
              activityStatus,
              activityLabel,
            } satisfies ChannelOption;
          })
          .sort((a, b) => {
            if (a.isOacAudit !== b.isOacAudit) {
              return a.isOacAudit ? -1 : 1;
            }
            if (a.isOacAudit && b.isOacAudit) {
              const ao = OAC_ACTIVITY_STATUS_ORDER[a.activityStatus ?? "compliant"];
              const bo = OAC_ACTIVITY_STATUS_ORDER[b.activityStatus ?? "compliant"];
              if (ao !== bo) return ao - bo;
            }
            return a.label.localeCompare(b.label);
          });
        setChannelOptions(list);
      } catch (e) {
        console.error(e);
        toast.current?.show({
          severity: "error",
          summary: "Failed to load channels",
          detail: oacPlaylistApi.getApiErrorDetail(e),
          life: 5000,
        });
      } finally {
        if (!cancelled) setChannelsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Prefill from ?channelId= when options are ready (or immediately if already set).
  useEffect(() => {
    if (!preselectedChannelId || channelsLoading) return;
    setChannelId(preselectedChannelId);
  }, [preselectedChannelId, channelsLoading]);

  useEffect(() => {
    if (!channelId) {
      setPreview(null);
      setPreviewError(null);
      setTitle("");
      setResult(null);
      return;
    }

    let cancelled = false;
    const loadPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      setResult(null);
      try {
        const data = await oacPlaylistApi.getPreview(channelId);
        if (cancelled) return;
        setPreview(data);
        setTitle(data.suggestedTitle);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setPreview(null);
          setPreviewError(oacPlaylistApi.getApiErrorDetail(e));
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    void loadPreview();
    return () => {
      cancelled = true;
    };
  }, [channelId]);

  const canCreate = useMemo(
    () =>
      Boolean(
        channelId && title.trim() && preview && !previewLoading && !creating
      ),
    [channelId, title, preview, previewLoading, creating]
  );

  const channelItemTemplate = (option: ChannelOption) => {
    if (!option) return null;
    return (
      <div
        className="flex align-items-center gap-2"
        style={{ flexWrap: "wrap" }}
      >
        {option.isOacAudit && option.activityStatus && (
          <ActivityTag status={option.activityStatus} />
        )}
        <span>{option.label.replace(/^\[OAC · [^\]]+\]\s*/, "")}</span>
      </div>
    );
  };

  const channelValueTemplate = (option: ChannelOption | null) => {
    if (!option) {
      return <span>Select a channel with content owner</span>;
    }
    return channelItemTemplate(option);
  };

  const handleCreate = () => {
    if (!channelId || !preview) return;

    confirmDialog({
      header: "Create playlist on YouTube?",
      message: (
        <div>
          <p style={{ marginTop: 0 }}>
            This creates a <strong>{privacyStatus}</strong> playlist on the live
            channel and adds up to the last 10 uploads.
          </p>
          <p>
            Channel: <code>{channelId}</code>
          </p>
          <p>
            Title: <strong>{title.trim()}</strong>
          </p>
          <p style={{ marginBottom: 0 }}>
            Videos to add: {preview.videos.length}
          </p>
        </div>
      ),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Create playlist",
      rejectLabel: "Cancel",
      accept: () => {
        void (async () => {
          setCreating(true);
          setResult(null);
          try {
            const created = await oacPlaylistApi.create({
              channelId,
              title: title.trim(),
              description: description.trim() || undefined,
              privacyStatus,
              videoIds: preview.videos.map((v) => v.videoId),
            });
            setResult(created);
            toast.current?.show({
              severity: created.failures.length > 0 ? "warn" : "success",
              summary:
                created.failures.length > 0
                  ? "Playlist created with some failures"
                  : "Playlist created",
              detail: created.playlistId,
              life: 5000,
            });
          } catch (e) {
            console.error(e);
            toast.current?.show({
              severity: "error",
              summary: "Create failed",
              detail: oacPlaylistApi.getApiErrorDetail(e),
              life: 6000,
            });
          } finally {
            setCreating(false);
          }
        })();
      },
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div>
        <h3 style={{ marginTop: 0, marginBottom: "0.5rem" }}>Create Playlist</h3>
        <p style={{ marginTop: 0, maxWidth: "48rem" }}>
          Create a YouTube playlist for a managed Official Artist Channel and
          populate it with the channel&apos;s last 10 uploads. Theme-based
          playlists and manual video editing are planned for a later release.
        </p>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          alignItems: "flex-end",
        }}
      >
        <div style={{ minWidth: "22rem", flex: "1 1 22rem" }}>
          <label htmlFor="oac-playlist-channel" className="block mb-2">
            Channel
          </label>
          {channelsLoading ? (
            <ProgressSpinner style={{ width: "32px", height: "32px" }} />
          ) : (
            <Dropdown
              inputId="oac-playlist-channel"
              value={channelId}
              options={channelOptions}
              onChange={(e) => setChannelId(e.value)}
              placeholder="Select a channel with content owner"
              filter
              filterBy="label,value"
              showClear
              className="w-full"
              itemTemplate={channelItemTemplate}
              valueTemplate={channelValueTemplate}
            />
          )}
          <small className="text-color-secondary">
            OAC Audit channels are listed first with their activity status.
          </small>
        </div>

        <div style={{ minWidth: "12rem" }}>
          <label htmlFor="oac-playlist-privacy" className="block mb-2">
            Privacy
          </label>
          <Dropdown
            inputId="oac-playlist-privacy"
            value={privacyStatus}
            options={PRIVACY_OPTIONS}
            onChange={(e) => setPrivacyStatus(e.value)}
            className="w-full"
          />
        </div>
      </div>

      {previewError && (
        <Message severity="error" text={previewError} className="w-full" />
      )}

      {previewLoading && (
        <div className="flex align-items-center gap-2">
          <ProgressSpinner style={{ width: "28px", height: "28px" }} />
          <span>Loading last uploads…</span>
        </div>
      )}

      {preview && !previewLoading && (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              maxWidth: "40rem",
            }}
          >
            <div>
              <label htmlFor="oac-playlist-title" className="block mb-2">
                Playlist title
              </label>
              <InputText
                id="oac-playlist-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full"
              />
              <small className="text-color-secondary">
                Default: {"{Brand Name} Ultimate Playlist"} (brand:{" "}
                {preview.brandName})
              </small>
            </div>
            <div>
              <label htmlFor="oac-playlist-desc" className="block mb-2">
                Description (optional)
              </label>
              <InputTextarea
                id="oac-playlist-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full"
                autoResize
              />
            </div>
          </div>

          <div>
            <h4 style={{ marginBottom: "0.5rem" }}>
              Last {preview.videos.length} uploads (read-only preview)
            </h4>
            {preview.videos.length === 0 ? (
              <Message
                severity="warn"
                text="No uploads found for this channel. You can still create an empty playlist."
              />
            ) : (
              <DataTable
                value={preview.videos}
                size="small"
                emptyMessage="No videos"
              >
                <Column
                  field="videoId"
                  header="Video ID"
                  style={{ width: "12rem" }}
                />
                <Column field="title" header="Title" />
                <Column
                  field="publishedAt"
                  header="Published"
                  style={{ width: "12rem" }}
                  body={(row) => formatDate(row.publishedAt)}
                />
              </DataTable>
            )}
          </div>

          <div>
            <Button
              label="Create playlist"
              icon="pi pi-plus"
              loading={creating}
              disabled={!canCreate}
              onClick={handleCreate}
            />
          </div>
        </>
      )}

      {result && (
        <Message
          severity={result.failures.length > 0 ? "warn" : "success"}
          className="w-full"
          content={
            <div>
              <div>
                Created <strong>{result.title}</strong> ({result.privacyStatus})
                — added {result.videosAdded.length} video(s)
                {result.failures.length > 0
                  ? `, ${result.failures.length} failed`
                  : ""}
                .
              </div>
              <div style={{ marginTop: "0.5rem" }}>
                Playlist ID: <code>{result.playlistId}</code>
              </div>
              <div style={{ marginTop: "0.35rem" }}>
                <a href={result.playlistUrl} target="_blank" rel="noreferrer">
                  Open on YouTube
                </a>
              </div>
              {result.failures.length > 0 && (
                <ul style={{ marginBottom: 0 }}>
                  {result.failures.map((f) => (
                    <li key={f.videoId}>
                      {f.videoId}: {f.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          }
        />
      )}

      <Message
        severity="info"
        className="w-full"
        text="Coming later: theme-based playlist templates and manual video ID editing."
      />
    </div>
  );
};

export default PlaylistCreationTab;
